<template>
    <div class="flex flex-col mt-5">
        <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
            <div
                class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
            >
                <table class="min-w-full divide-y divide-gray-200">
                    <thead>
                        <tr>
                            <th
                                class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                            >
                                {{ $tc('messages.gamification_items', 1) }}
                            </th>
                            <th
                                class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                            >
                                {{ $t('messages.quantity') }} ({{ totalQuantity }})
                            </th>
                            <th
                                class="px-6 py-3 bg-gray-50 text-right text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                            >
                                {{ $t('messages.total') }}
                            </th>
                            <th
                                class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                            >
                                <span class="sr-only">{{ $t('messages.actions') }}</span>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr class="bg-white" v-for="(row, index) in items" :key="index">
                            <td
                                class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                            >
                                <div class="flex gap-x-2 items-center">
                                    <div class="h-10 w-10 bg-white">
                                        <img
                                            :src="images[row.gamification_item_id]"
                                            :alt="`${row.gamification_item_id}`"
                                            v-if="row.gamification_item_id"
                                            class="object-contain h-10 w-10"
                                        />
                                    </div>
                                    <FormSelect
                                        required
                                        v-model.number="row.gamification_item_id"
                                        :options="filteredItems(row.gamification_item_id)"
                                        class="w-full"
                                        optgroups
                                    />
                                </div>
                                <FormError
                                    :value="errors[`requestedItems.${index}.gamification_item_id`]"
                                    class="mt-2 max-w-sm"
                                />
                            </td>
                            <td class="max-w-sm truncate px-6 py-4 text-sm leading-5 font-medium text-gray-900">
                                <input
                                    type="number"
                                    :name="`requestedItems[${index}][quantity]`"
                                    v-model="row.quantity"
                                    class="w-20 border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 sm:max-w-xs sm:text-sm"
                                />
                                <FormError :value="errors[`requestedItems.${index}.quantity`]" class="mt-2 max-w-sm" />
                            </td>
                            <td
                                class="max-w-sm truncate px-6 py-4 text-sm leading-5 font-medium text-gray-900 text-right tabular-nums"
                            >
                                <div v-if="row.gamification_item_id">
                                    <div v-if="prices[row.gamification_item_id] === null">0</div>
                                    <div v-else-if="prices[row.gamification_item_id]">
                                        <span
                                            >{{ row.quantity }} &times; {{ prices[row.gamification_item_id] }} =
                                            &nbsp;</span
                                        >
                                        <span> {{ row.quantity * prices[row.gamification_item_id] }} </span>
                                    </div>
                                </div>
                            </td>
                            <td
                                class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                            >
                                <button
                                    type="button"
                                    @click="deleteRow(index)"
                                    class="inline-flex items-center px-1.5 py-1.5 border border-transparent text-xs font-medium rounded text-gray-700 bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                >
                                    <svg viewBox="0 0 20 20" fill="currentColor" class="trash w-4 h-4">
                                        <path
                                            fill-rule="evenodd"
                                            d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                                            clip-rule="evenodd"
                                        ></path>
                                    </svg>
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div class="mt-3" v-if="items.length < maxItems">
                <PlusButton @click="addRow()" />
            </div>
        </div>
    </div>
</template>
<script setup>
import FormError from '@/components/Forms/FormError.vue';
import PlusButton from '@/components/Forms/PlusButton.vue';
import FormSelect from '@/components/Forms/FormSelect.vue';
import { computed, onMounted, ref, watch } from 'vue';

const props = defineProps({
    modelValue: { required: true, type: Array },
    options: { required: true, type: Object },
    errors: { type: Object, required: false },
});

const emit = defineEmits(['update:modelValue', 'cost']);

const items = ref(props.modelValue);
const addRow = () => {
    items.value.push({
        gamification_item_id: null,
        quantity: 1,
    });
};
const deleteRow = (index) => {
    items.value.splice(index, 1);
    if (items.value.length === 0) {
        addRow();
    }
};
const filteredItems = (id) => {
    const ids = items.value.filter((i) => i.gamification_item_id != id).map((i) => +i.gamification_item_id);
    return props.options.items
        .map((g) => {
            return {
                label: g.label,
                options: g.options.filter((i) => !ids.includes(i.value)),
            };
        })
        .filter((g) => g.options.length);
};
watch(items, (newVal) => {
    emit('update:modelValue', newVal);
});
const maxItems = computed(() => props.options.items.flatMap((i) => i.options).length);
const prices = computed(() => {
    const items = props.options.items.flatMap((i) => i.options);
    let map = {};
    items.forEach((i) => {
        map[i.value] = i.price;
    });

    return map;
});
const totalCost = computed(() =>
    items.value
        .filter((i) => i.gamification_item_id)
        .map((i) => {
            return i.quantity * prices.value[i.gamification_item_id];
        })
        .reduce((a, b) => a + b, 0),
);

watch(
    totalCost,
    (newVal) => {
        emit('cost', newVal);
    },
    { immediate: true },
);

const totalQuantity = computed(() => items.value.reduce((a, b) => a + b.quantity, 0));
const images = computed(() => {
    const items = props.options.items.flatMap((i) => i.options);
    let map = {};
    items.forEach((i) => {
        map[i.value] = i.image_path;
    });

    return map;
});
onMounted(() => {
    if (items.value.length === 0) {
        addRow();
    }
});
</script>
